const MS_PER_DAY = 1000 * 60 * 60 * 24
const COMPLETE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export interface PlainDate {
  year: number
  month: number
  day: number
}

export interface AgeResult {
  years: number
  months: number
  days: number
  totalDays: number
  birthDate: Date
  targetDate: Date
  nextBirthday: Date
  daysToNextBirthday: number
}

export type AgeError =
  | 'missing_birth_date'
  | 'invalid_birth_date'
  | 'invalid_target_date'
  | 'future_birth_date'
  | 'future_target_date'
  | 'target_before_birth'

export interface AgeValidationState {
  birthBlurred: boolean
  targetBlurred: boolean
}

export interface AgeDerivation {
  result: AgeResult | null
  error: AgeError | null
}

export function parseAgeInputDate(value: string): PlainDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day, 12)

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }

  return { year, month, day }
}

export function agePlainDateToDate(value: PlainDate): Date {
  return new Date(value.year, value.month - 1, value.day, 12)
}

export function ageDateToInput(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function deriveAge(
  birthInput: string,
  targetInput: string,
  todayInput: string,
  validation: AgeValidationState,
): AgeDerivation {
  if (!birthInput) {
    return {
      result: null,
      error: validation.birthBlurred ? 'missing_birth_date' : null,
    }
  }

  const birth = parseAgeInputDate(birthInput)
  if (!birth) {
    return {
      result: null,
      error: shouldShowDateError(birthInput, validation.birthBlurred)
        ? 'invalid_birth_date'
        : null,
    }
  }

  const targetValue = targetInput || todayInput
  const target = parseAgeInputDate(targetValue)
  if (!target) {
    return {
      result: null,
      error: shouldShowDateError(targetInput, validation.targetBlurred)
        ? 'invalid_target_date'
        : null,
    }
  }

  const today = parseAgeInputDate(todayInput)
  if (!today) return { result: null, error: 'invalid_target_date' }

  const birthDate = agePlainDateToDate(birth)
  const targetDate = agePlainDateToDate(target)
  const todayDate = agePlainDateToDate(today)

  if (compareAgeDates(birthDate, todayDate) > 0) {
    return { result: null, error: 'future_birth_date' }
  }
  if (compareAgeDates(targetDate, todayDate) > 0) {
    return { result: null, error: 'future_target_date' }
  }
  if (compareAgeDates(targetDate, birthDate) < 0) {
    return { result: null, error: 'target_before_birth' }
  }

  return {
    result: calculateAge(birthDate, targetDate),
    error: null,
  }
}

export function formatAgeDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function shouldShowDateError(value: string, blurred: boolean): boolean {
  return blurred || COMPLETE_DATE_PATTERN.test(value)
}

function compareAgeDates(a: Date, b: Date): number {
  const aTime = new Date(a.getFullYear(), a.getMonth(), a.getDate(), 12).getTime()
  const bTime = new Date(b.getFullYear(), b.getMonth(), b.getDate(), 12).getTime()
  return aTime - bTime
}

function diffAgeDays(start: Date, end: Date): number {
  return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY)
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate()
}

function addYearsClamped(date: Date, years: number): Date {
  const targetYear = date.getFullYear() + years
  const targetMonth = date.getMonth()
  const targetDay = Math.min(date.getDate(), daysInMonth(targetYear, targetMonth))
  return new Date(targetYear, targetMonth, targetDay, 12)
}

function addMonthsClamped(date: Date, months: number): Date {
  const targetYear = date.getFullYear()
  const targetMonth = date.getMonth() + months
  const year = targetYear + Math.floor(targetMonth / 12)
  const month = ((targetMonth % 12) + 12) % 12
  const day = Math.min(date.getDate(), daysInMonth(year, month))
  return new Date(year, month, day, 12)
}

function calculateAge(birthDate: Date, targetDate: Date): AgeResult {
  let years = targetDate.getFullYear() - birthDate.getFullYear()
  let yearAnchor = addYearsClamped(birthDate, years)

  if (compareAgeDates(yearAnchor, targetDate) > 0) {
    years -= 1
    yearAnchor = addYearsClamped(birthDate, years)
  }

  let months = 0
  while (
    months < 11 &&
    compareAgeDates(addMonthsClamped(yearAnchor, months + 1), targetDate) <= 0
  ) {
    months += 1
  }

  const monthAnchor = addMonthsClamped(yearAnchor, months)
  const days = diffAgeDays(monthAnchor, targetDate)
  const nextBirthday = nextBirthdayAfter(birthDate, targetDate)

  return {
    years,
    months,
    days,
    totalDays: diffAgeDays(birthDate, targetDate),
    birthDate,
    targetDate,
    nextBirthday,
    daysToNextBirthday: diffAgeDays(targetDate, nextBirthday),
  }
}

function nextBirthdayAfter(birthDate: Date, targetDate: Date): Date {
  const year = targetDate.getFullYear()
  let next = addYearsClamped(birthDate, year - birthDate.getFullYear())

  if (compareAgeDates(next, targetDate) <= 0) {
    next = addYearsClamped(birthDate, year - birthDate.getFullYear() + 1)
  }

  return next
}
